import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import wmsBaseService from '../../../service/wms/wmsBase.service';
import { useLoader } from '../../../provider/LoaderProvider';
import { dataGridPremiumColumns } from '../../../utils/helper';
import DeleteButton from '../Components/DeleteButton';
import NextButton from '../Components/NextButton';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import { DataGridIdentifiers } from '../../Interface/Datagrid/DataGridIdentifiers';
import DatagridPremiumUi from '../../Interface/DatagridPremmium/DataGridPremiumUI';
import { WmsType } from '../../../config/wms/wmsType';

const wmsType = WmsType.Delivery;

const InprogressDelivery = ({ autocompleteData }) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(false);
  const { showLoader, hideLoader } = useLoader();

  const handleFetchData = async () => {
    setLoading(true);
    showLoader();
    try {
      const response = await wmsBaseService.getData({ wmsType });
      setData(response?.rows ?? []);
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const handleContinue = (wmsEntry) => {
    navigate(`../deliveryDoc/${wmsEntry}`);
  };

  const handleDelete = async (wmsEntry) => {
    showLoader();
    try {
      await wmsBaseService.deleteData(wmsEntry, wmsType);
      handleFetchData();
      return addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
    } catch (e) {
      addNotification({
        content: 'Hiba történt a törlés során!',
        type: NotificationType.ERROR,
      });
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    handleFetchData();
  }, []);

  const warehouseMap = useMemo(
    () => new Map((autocompleteData?.warehouse ?? []).map((warehouse) => [warehouse.code, warehouse])),
    [autocompleteData]
  );

  const companyMap = useMemo(
    () => new Map((autocompleteData?.company ?? []).map((company) => [company.id, company])),
    [autocompleteData]
  );

  const userMap = useMemo(
    () => new Map((autocompleteData?.user ?? []).map((user) => [user.userId, user])),
    [autocompleteData]
  );

  const columns = dataGridPremiumColumns([
    {
      field: 'id',
      headerName: '#',
      flex: 0.25,
    },
    {
      field: 'createdAt',
      headerName: 'Folyamatban',
      flex: 1,
      renderCell: (params) => {
        const company = companyMap.get(params.row?.companyId);
        const warehouse = warehouseMap.get(params.row?.whsCode);
        const createdByUser = userMap.get(params.row?.createdBy);
        const companyLabel = [company?.code ?? params.row?.companyCode, company?.name ?? params.row?.companyName]
          .filter(Boolean)
          .join(' - ');
        const warehouseLabel = [params.row?.whsCode, warehouse?.name].filter(Boolean).join(' - ');
        const createdByLabel =
          createdByUser?.nickName ??
          params.row?.userName ??
          params.row?.UserName ??
          params.row?.createdByName ??
          params.row?.createdBy ??
          '-';

        return (
          <div>
            <Typography variant="body1" fontWeight="bold">
              {warehouseLabel || '-'}
            </Typography>
            <Typography variant="body2">ÜP: {companyLabel || '-'}</Typography>
            {params.row?.baseDocNum ? (
              <Typography variant="body2">Forrásbizonylat: {params.row.baseDocNum}</Typography>
            ) : null}
            <Typography variant="body2">Létrehozta: {createdByLabel}</Typography>
            <Typography variant="body2">{dayjs(params.row.createdAt).format('YYYY.MM.DD HH:mm')}</Typography>
          </div>
        );
      },
    },
    {
      field: 'operators',
      headerName: '',
      flex: 0.45,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <NextButton onClick={() => handleContinue(params.row.id)} />
          <DeleteButton onClick={() => handleDelete(params.row.id)} />
        </Box>
      ),
    },
  ]);

  return (
    <Box sx={{ pb: 2, pt: 3, px: 0, width: '100%' }}>
      <DatagridPremiumUi
        datagridIdentifier={DataGridIdentifiers.wmsDeliveryInProgress}
        rows={data}
        columns={columns}
        loading={loading}
        sx={{ width: '100%' }}
        getRowHeight={() => 'auto'}
        headerFilters={false}
        slots={{
          toolbar: null,
        }}
      />
    </Box>
  );
};

export default InprogressDelivery;
